/**
 * Source unique des listes de dimensions du tiroir pneu — construites à partir des fiches
 * RÉELLEMENT PUBLIÉES dans la collection "Pneus", et non de constantes écrites en dur.
 *
 * Le défaut corrigé ici (11/09/2026) : devis-widget.js et devis-rdv-widget.js portaient chacun
 * leur copie de PNEU_CHARGES = ['89','93'] et PNEU_VITESSES = ['H','V','W','Y','Z']. Sur les
 * 89 fiches publiées, 82 avaient un indice absent de ces listes — dont les trois 175/65 R14, en
 * 86T et 82H. Le client ne pouvait donc pas exprimer son besoin, et la page résultats, qui écarte
 * à juste titre tout pneu d'indice INFÉRIEUR au besoin, ne renvoyait rien : « pas de prix
 * disponible ». Aucune fiche n'était en cause, aucun metafield n'était vide.
 *
 * Ce fichier est chargé par les deux tiroirs, qui sont des IIFE indépendantes sans état partagé
 * (même schéma que mauto-calendrier.js pour les règles de rendez-vous). Il doit être chargé
 * AVANT eux : les scripts différés s'exécutent dans l'ordre du document.
 *
 * Deux niveaux de données, et la distinction est importante :
 *
 *   1. L'INDEX DU CATALOGUE, chargé depuis /collections/pneus?view=pneus-index. C'est lui qui
 *      alimente les listes affichées par défaut, dimension par dimension. Rien n'y est écrit en
 *      dur : si une fiche existe, ses indices sont proposables.
 *
 *   2. LES ÉCHELLES NORMALISÉES ci-dessous (ETRTO). Ce ne sont pas des valeurs « de stock »
 *      devinées, ce sont les barèmes officiels, les mêmes pour tous les pneumatiques du monde.
 *      Elles servent à deux choses, et à rien d'autre :
 *        — le lien « Voir tous les indices », pour qu'un client dont le véhicule EXIGE un indice
 *          que nous ne stockons pas puisse quand même le saisir. Sans cette porte de sortie, un
 *          client ayant besoin de 91V devant un catalogue en 88H et 94W choisirait 88H « parce
 *          que c'est proposé » et repartirait avec un pneu sous-dimensionné. On préfère
 *          l'envoyer vers un devis sous 24 h ;
 *        — le repli si l'index ne se charge pas (réseau, thème en cours de publication) : le
 *          parcours reste utilisable au lieu de se bloquer.
 */
(function () {
  'use strict';

  var INDEX_URL = '/collections/pneus?view=pneus-index';
  var CACHE_CLE = 'mautoPneuIndex';
  var CACHE_MS = 30 * 60 * 1000;   // le catalogue bouge rarement, mais une correction de metafield
                                   // doit se voir dans la demi-heure sans vider son navigateur.

  // Échelle des indices de vitesse, ordre CROISSANT. Piège classique : l'ordre n'est PAS
  // alphabétique, H se place entre U et V. Doit rester cohérent avec VITESSE_RANG dans
  // assets/pneus-resultats.js, qui l'utilise pour la règle de compatibilité.
  // « Z » n'y figure volontairement pas : ce n'est pas un indice mais un marquage de carcasse
  // (ZR), il n'a pas de rang comparable. Il était proposé par l'ancienne liste en dur et rendait
  // mécaniquement TOUT pneu incompatible, puisque son rang valait 0.
  var ECHELLE_VITESSES = ['L', 'M', 'N', 'P', 'Q', 'R', 'S', 'T', 'U', 'H', 'V', 'W', 'Y'];

  // Indices de charge du tourisme et de l'utilitaire léger (barème ETRTO).
  var ECHELLE_CHARGES = (function () {
    var out = [];
    for (var i = 60; i <= 130; i++) out.push(String(i));
    return out;
  })();

  var ECHELLE_LARGEURS = ['125', '135', '145', '155', '165', '175', '185', '195', '205', '215',
    '225', '235', '245', '255', '265', '275', '285', '295', '305', '315', '325', '335'];
  var ECHELLE_HAUTEURS = ['25', '30', '35', '40', '45', '50', '55', '60', '65', '70', '75', '80', '82', '85'];
  var ECHELLE_DIAMETRES = ['12', '13', '14', '15', '16', '17', '18', '19', '20', '21', '22'];

  function txt(v) {
    return v === null || v === undefined ? '' : String(v).trim();
  }

  // Tri numérique pour les largeurs/hauteurs/diamètres/charges, tri par rang pour les vitesses.
  function trierNum(liste) {
    return liste.slice().sort(function (a, b) { return Number(a) - Number(b); });
  }
  function trierVitesses(liste) {
    return liste.slice().sort(function (a, b) {
      return ECHELLE_VITESSES.indexOf(a) - ECHELLE_VITESSES.indexOf(b);
    });
  }

  // Union en préservant l'ordre de l'échelle, puis en ajoutant à la fin ce que le catalogue
  // contient et que l'échelle ignore. Ce dernier cas ne devrait pas arriver, mais s'il arrive
  // (nouvel indice, saisie inattendue) la fiche reste atteignable : c'est tout l'objet du
  // correctif, aucune valeur du catalogue ne doit pouvoir disparaître des listes.
  function union(echelle, duCatalogue) {
    var out = echelle.slice();
    duCatalogue.forEach(function (v) { if (out.indexOf(v) === -1) out.push(v); });
    return out;
  }

  var promesse = null;

  function lireCache() {
    try {
      var brut = window.sessionStorage.getItem(CACHE_CLE);
      if (!brut) return null;
      var o = JSON.parse(brut);
      if (!o || !o.le || (Date.now() - o.le) > CACHE_MS) return null;
      return o.fiches;
    } catch (e) { return null; }
  }

  function ecrireCache(fiches) {
    try {
      window.sessionStorage.setItem(CACHE_CLE, JSON.stringify({ le: Date.now(), fiches: fiches }));
    } catch (e) { /* mode privé, quota : sans conséquence, on rechargera */ }
  }

  function chargerPage(page) {
    var url = INDEX_URL + (page > 1 ? '&page=' + page : '');
    // Surtout ne rien mettre dans Accept. Avec `Accept: text/plain`, Shopify répond 404 sur une
    // URL ?view= — vérifié le 11/09/2026 : text/plain → 404, */* et text/html → 200 avec le bon
    // contenu. Le défaut du navigateur convient, la réponse est lue en texte de toute façon.
    return fetch(url)
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.text();
      })
      .then(function (t) { return JSON.parse(t.trim()); });
  }

  /**
   * Renvoie une promesse résolue avec { fiches: [...], erreur: bool }.
   * Chaque fiche est un objet { largeur, hauteur, diametre, charge, vitesse, saison } en chaînes.
   * Ne rejette jamais : en cas d'échec on renvoie une liste vide et `erreur: true`, le tiroir
   * bascule alors sur les échelles normalisées plutôt que de bloquer le client.
   */
  function charger() {
    if (promesse) return promesse;
    var enCache = lireCache();
    if (enCache) {
      promesse = Promise.resolve({ fiches: enCache, erreur: false });
      return promesse;
    }
    promesse = chargerPage(1)
      .then(function (p1) {
        var pages = Number(p1.pages) || 1;
        if (pages <= 1) return [p1];
        var suite = [];
        for (var n = 2; n <= pages; n++) suite.push(chargerPage(n));
        return Promise.all(suite).then(function (rest) { return [p1].concat(rest); });
      })
      .then(function (lots) {
        var fiches = [];
        lots.forEach(function (lot) {
          (lot.fiches || []).forEach(function (f) {
            fiches.push({
              largeur: txt(f[0]), hauteur: txt(f[1]), diametre: txt(f[2]),
              charge: txt(f[3]), vitesse: txt(f[4]).toUpperCase(), saison: txt(f[5]),
            });
          });
        });
        ecrireCache(fiches);
        return { fiches: fiches, erreur: false };
      })
      .catch(function () {
        promesse = null;   // un échec réseau ne doit pas condamner la session entière
        return { fiches: [], erreur: true };
      });
    return promesse;
  }

  // Filtre les fiches sur les critères déjà choisis par le client. `sel` accepte largeur,
  // hauteur, diametre — jamais les indices : l'indice saisi par le client est son BESOIN, un
  // pneu d'indice supérieur reste montable (cf. compatibiliteIndices dans pneus-resultats.js).
  // Filtrer les vitesses proposées par la charge déjà choisie masquerait des pneus valides.
  function filtrer(index, sel) {
    return (index && index.fiches ? index.fiches : []).filter(function (f) {
      if (sel.largeur && f.largeur !== String(sel.largeur)) return false;
      if (sel.hauteur && f.hauteur !== String(sel.hauteur)) return false;
      if (sel.diametre && f.diametre !== String(sel.diametre)) return false;
      return true;
    });
  }

  function distinctes(fiches, champ) {
    var vues = {}, out = [];
    fiches.forEach(function (f) {
      var v = f[champ];
      if (v && !vues[v]) { vues[v] = 1; out.push(v); }
    });
    return out;
  }

  /**
   * Le seul point d'entrée utilisé par les tiroirs.
   *   champ : 'largeur' | 'hauteur' | 'diametre' | 'charge' | 'vitesse'
   *   sel   : { largeur, hauteur, diametre } déjà choisis
   * Renvoie { dispo, toutes } :
   *   dispo  = valeurs présentes au catalogue pour cette sélection (liste affichée par défaut)
   *   toutes = échelle normalisée complète, union faite avec `dispo` (lien « Voir tout »)
   * Si `dispo` est vide — dimension que le garage ne référence pas — l'appelant affiche
   * directement `toutes` : le client doit pouvoir saisir son besoin pour obtenir un devis.
   */
  function options(index, champ, sel) {
    var fiches = filtrer(index, sel || {});
    var brutes = distinctes(fiches, champ);
    var dispo, echelle;
    if (champ === 'vitesse') {
      dispo = trierVitesses(brutes);
      echelle = ECHELLE_VITESSES;
    } else {
      dispo = trierNum(brutes);
      echelle = champ === 'charge' ? ECHELLE_CHARGES
        : champ === 'largeur' ? ECHELLE_LARGEURS
        : champ === 'hauteur' ? ECHELLE_HAUTEURS
        : ECHELLE_DIAMETRES;
    }
    return { dispo: dispo, toutes: union(echelle, dispo) };
  }

  window.MautoPneuDimensions = {
    charger: charger,
    options: options,
    ECHELLE_CHARGES: ECHELLE_CHARGES,
    ECHELLE_VITESSES: ECHELLE_VITESSES,
    ECHELLE_LARGEURS: ECHELLE_LARGEURS,
    ECHELLE_HAUTEURS: ECHELLE_HAUTEURS,
    ECHELLE_DIAMETRES: ECHELLE_DIAMETRES,
  };
})();
